import { Injectable, OnDestroy } from "@angular/core";
import { Observable, BehaviorSubject, of, Subscription } from "rxjs";
import { map, catchError, switchMap, finalize } from "rxjs/operators";
import { AuthModel } from "../_models/auth.model";
import { AccountModel } from "../_models/account.model";
import { AuthHTTPService } from "./auth-http/auth-http.service";
import { Router, ActivatedRoute } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  // public fields
  currentUser$: Observable<AccountModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<AccountModel>;
  isLoadingSubject: BehaviorSubject<boolean>;
  returnUrl: string;
  userInfo: any;

  get currentUserValue(): AccountModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: AccountModel) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<AccountModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  ngOnInit(): void {
    // get return url from route parameters or default to '/'
    this.returnUrl =
      this.route.snapshot.queryParams["returnUrl".toString()] || "/";
  }

  // public methods
  login(req: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(req).pipe(
      map((response: any) => {
        if (response.result) {
          //this.getUserInfo();
          localStorage.setItem("firstName", response.result.firstName);
          localStorage.setItem("lastName", response.result.lastName);
          localStorage.setItem("username", response.result.username);
          localStorage.setItem("email", response.result.email);
          localStorage.setItem("token", response.result.token);
          localStorage.setItem("refreshToken", response.result.refreshToken);
          localStorage.setItem("tokenValidFrom", response.result.validFrom);
          localStorage.setItem("tokenValidTo", response.result.validTo);
          localStorage.setItem("accountId", response.result.accountId);
          localStorage.setItem("roleName", response.result.roleName);
          sessionStorage.setItem("firstName", response.result.firstName);
          sessionStorage.setItem("lastName", response.result.lastName);

          const authInfo = new AuthModel();
          authInfo.authToken = response.result.token;
          authInfo.refreshToken = response.result.refreshToken;
          authInfo.expiresIn = response.result.validTo;

          const result = this.setAuthFromLocalStorage(authInfo);
        }

        return response;
      }),
      catchError((err) => {
        return of(err.error);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    this.clearLocalStorage();
    this.router.navigate(["/auth/splash-screen"], {
      queryParams: {},
    });
  }

  clearLocalStorage() {
    sessionStorage.removeItem("firstName");
    sessionStorage.removeItem("lastName");
    localStorage.removeItem("firstName");
    localStorage.removeItem("lastName");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("phoneNumber");
    localStorage.removeItem("accountId");
    localStorage.removeItem("tokenValidFrom");
    localStorage.removeItem("tokenValidTo");
    localStorage.removeItem("roleName");
    localStorage.removeItem("profileUrl");
  }

  // need create new user then login
  registration(accountModel: AccountModel): Observable<any> {
    var loginObj = {
      username: accountModel.user.email,
      password: accountModel.user.password,
    };

    this.isLoadingSubject.next(true);
    
    return this.authHttpService.accountSignUp(accountModel).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err) => {
        return of(err.error);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(req: any): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(req)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      return true;
    }
    return false;
  }

  getUserInfo() {
    this.authHttpService.userInfo().subscribe((Response) => {
      if (Response.result) {
        this.userInfo = Response.result;
        localStorage.setItem('profileUrl', this.userInfo.profileUrl);
        localStorage.setItem("accountId", this.userInfo.accountId);
        localStorage.setItem("phoneNumber", this.userInfo.phoneNumber);
      }
    });
  }

  private getAuthFromLocalStorage(): AuthModel {
    try {
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  refreshToken(req: any) {
    this.authHttpService.refreshToken(req).
    subscribe(
      response => {
        if (response.result) {
          this.getUserInfo();
          localStorage.setItem("firstName", response.result.firstName);
          localStorage.setItem("lastName", response.result.lastName);
          localStorage.setItem("username", response.result.username);
          localStorage.setItem("email", response.result.email);
          localStorage.setItem("token", response.result.token);
          localStorage.setItem("refreshToken", response.result.refreshToken);
          localStorage.setItem("tokenValidFrom", response.result.validFrom);
          localStorage.setItem("tokenValidTo", response.result.validTo);
          localStorage.setItem("accountId", response.result.accountId);

          sessionStorage.setItem("firstName", response.result.firstName);
          sessionStorage.setItem("lastName", response.result.lastName);

          const authInfo = new AuthModel();
          authInfo.authToken = response.result.token;
          authInfo.refreshToken = response.result.refreshToken;
          authInfo.expiresIn = response.result.validTo;
          this.setAuthFromLocalStorage(authInfo);
        }
        else
        {
          this.logout();
        }
      },
      err => {
        this.logout();
      },
      () => console.log('complete')
    );
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  getcountryList(): Observable<any> {
    return this.http.get(environment.API + "countries");
  }

  getstateList(): Observable<any> {
    return this.http.get(environment.API + "states");
  }

  getCityList(): Observable<any> {
    return this.http.get(environment.API + "cities"  );
  }

  getUserEmail(email:any): Observable<any> {
    let data = {
      'email' : email
    }
    return this.http.post(environment.API + "users/get-account-by-email", data);
  }

}
